import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';

// Обновленный контейнер хедера с улучшенной стилизацией
const HeaderContainer = styled(motion.header)`
  position: sticky;
  top: 0;
  left: 0;
  width: 100%;
  height: 76px; /* Увеличенная высота для лучших отступов */
  background: rgba(18, 18, 18, 0.85);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 24px;
  z-index: 1000;
  box-shadow: 0 2px 20px rgba(0, 0, 0, 0.3);
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  transition: all 0.5s ease;
`;

// Улучшенный контейнер для логотипа с лучшим выравниванием
const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 14px; /* Увеличенное расстояние между иконкой и текстом */
  padding: 10px 0; /* Отступы сверху и снизу */
`;

// Улучшенная иконка приложения с красивыми переливами
const AppleStyleAppLogo = styled.div`
  width: 46px;
  height: 46px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  box-shadow: 
    0 6px 16px rgba(104, 50, 220, 0.3),
    0 0 0 1px rgba(255, 255, 255, 0.1) inset;
  
  /* Улучшенный переливающийся эффект */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(
      135deg, 
      rgba(104, 50, 220, 0.2) 0%,
      rgba(255, 255, 255, 0.1) 50%, 
      rgba(104, 50, 220, 0.2) 100%
    );
    z-index: 3;
    animation: colorShift 5s infinite alternate;
  }
  
  /* Блик как на иконках Apple */
  &::after {
    content: '';
    position: absolute;
    top: -100%;
    left: -100%;
    width: 300%;
    height: 300%;
    background: linear-gradient(
      135deg, 
      transparent 20%, 
      rgba(255, 255, 255, 0.15) 50%,
      transparent 80%
    );
    transform: rotate(25deg);
    animation: shine 3s infinite ease-in-out;
    z-index: 2;
  }
  
  @keyframes shine {
    0% {
      transform: rotate(35deg) translate(-80%, -80%);
    }
    50% {
      transform: rotate(35deg) translate(0%, 0%);
    }
    100% {
      transform: rotate(35deg) translate(80%, 80%);
    }
  }
  
  @keyframes colorShift {
    0% {
      opacity: 0.5;
    }
    50% {
      opacity: 0.8;
    }
    100% {
      opacity: 0.5;
    }
  }
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    z-index: 1;
    border-radius: 10px; /* Небольшое скругление для изображения */
  }
`;

// Улучшенное название приложения с плавными переходами
const AppName = styled.h1`
  font-family: -apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif;
  font-weight: 700;
  font-size: 19px; /* Немного увеличенный размер */
  margin: 0;
  letter-spacing: -0.5px;
  background: linear-gradient(90deg, #fff 0%, rgba(255, 255, 255, 0.7) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  transition: all 0.5s ease;
  text-shadow: 0 0 20px rgba(255, 255, 255, 0.1);
`;

// Контейнер для правой части с кнопками
const ActionsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

// Стили для кнопок Terms и Privacy
const LegalButton = styled(motion.button)`
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: rgba(255, 255, 255, 0.8);
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: -apple-system, BlinkMacSystemFont, "SF Pro Text", sans-serif;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.4);
    color: white;
    transform: translateY(-1px);
  }
  
  /* Стили для светлой темы при скролле */
  &.scrolled {
    border-color: rgba(0, 0, 0, 0.2);
    color: rgba(0, 0, 0, 0.8);
    
    &:hover {
      background: rgba(0, 0, 0, 0.1);
      border-color: rgba(0, 0, 0, 0.4);
      color: #000;
    }
  }
  
  @media (max-width: 768px) {
    display: none; // Скрываем на мобильных устройствах
  }
`;

// Обновленный MenuButton с лучшим эффектом наведения
const MenuButton = styled(motion.button)`
  background: transparent;
  border: none;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0);
    transition: all 0.3s ease;
    z-index: -1;
  }
  
  &:hover {
    transform: scale(1.05);
    
    &::after {
      background: rgba(255, 255, 255, 0.1);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }
  }
`;

// Улучшенная кнопка загрузки в минималистичном стиле App Store
const AppStoreDownloadButton = styled(motion.button)`
  background: linear-gradient(135deg, #0070E0, #10A5F5);
  color: white;
  border: none;
  border-radius: 16px; /* Ещё меньшее скругление */
  padding: 7px 14px; /* Ещё более компактные отступы */
  font-weight: 500;
  font-size: 12.5px; /* Меньший размер текста */
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px; /* Меньший отступ между иконкой и текстом */
  box-shadow: 0 2px 8px rgba(0, 102, 204, 0.12);
  transition: all 0.25s ease;
  font-family: -apple-system, BlinkMacSystemFont, "SF Pro Text", sans-serif;
  letter-spacing: -0.2px;
  
  /* Убран переливающийся эффект */
  
  svg {
    width: 14px; /* Ещё меньшая иконка */
    height: 14px;
  }
  
  &:hover {
    transform: translateY(-1px);
    background: linear-gradient(135deg, #0074E5, #20B0FF);
    box-shadow: 0 4px 10px rgba(0, 102, 204, 0.18);
  }
  
  &:active {
    transform: translateY(0px);
  }
`;

// Компонент хедера с эффектом прокрутки
const Header = () => {
  const [scrolled, setScrolled] = React.useState(false);
  const navigate = useNavigate();
  
  React.useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 20;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [scrolled]);

  return (
    <HeaderContainer
      initial={{ opacity: 0, y: -20 }}
      animate={{ 
        opacity: 1, 
        y: 0,
        background: scrolled 
          ? 'rgba(255, 255, 255, 0.85)' 
          : 'rgba(18, 18, 18, 0.85)'
      }}
      transition={{ duration: 0.4 }}
      style={{ 
        boxShadow: scrolled 
          ? '0 2px 20px rgba(0, 0, 0, 0.15)' 
          : '0 2px 20px rgba(0, 0, 0, 0.3)',
        borderBottom: scrolled
          ? '1px solid rgba(0, 0, 0, 0.08)'
          : '1px solid rgba(255, 255, 255, 0.05)'
      }}
    >
      <LogoContainer>
        <AppleStyleAppLogo>
          <img src={require('../assets/appIcon.png')} alt="Chef Kats AI" />
        </AppleStyleAppLogo>
        <AppName style={{ 
          color: scrolled ? '#333' : 'white',
          background: scrolled 
            ? 'linear-gradient(90deg, #333 0%, rgba(51, 51, 51, 0.8) 100%)' 
            : 'linear-gradient(90deg, #fff 0%, rgba(255, 255, 255, 0.7) 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent'
        }}>
          Chef Kats AI
        </AppName>
      </LogoContainer>
      
      <ActionsContainer>
        <LegalButton
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => navigate('/terms')}
          className={scrolled ? 'scrolled' : ''}
        >
          Terms
        </LegalButton>
        
        <LegalButton
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => navigate('/privacy')}
          className={scrolled ? 'scrolled' : ''}
        >
          Privacy
        </LegalButton>
         
        <AppStoreDownloadButton
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          as="a"
          href="#"
          onClick={(e) => {
            e.preventDefault(); // чтобы не перейти мгновенно
            if (typeof window !== 'undefined' && window.gtag_report_conversion) {
              window.gtag_report_conversion('https://apps.apple.com/us/app/chefkats-ai-snap-cook/id6739225693');
            } else {
              // fallback, если по какой-то причине не сработало
              window.location.href = 'https://apps.apple.com/us/app/chefkats-ai-snap-cook/id6739225693';
            }
          }}
          target="_blank"
          rel="noopener noreferrer"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="white">
            <path d="M12 15.5l-4.5-4.5h3V6h3v5h3L12 15.5zm-7 2h14v2H5v-2z"/>
          </svg>
          Download
        </AppStoreDownloadButton>
      </ActionsContainer>
    </HeaderContainer>
  );
};

export default Header; 